import { cn } from '@/utils/utils';
import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';
import { LoadingSpinner } from '../Loading';

const buttonVariants = cva(
  'inline-flex items-center relative gap-2 font-semibold justify-center whitespace-nowrap rounded-md text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground',
        destructive: 'bg-destructive text-destructive-solid',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        onboarding:
          'bg-none border-2 border-border text-foreground hover:bg-none cursor-pointer hover:scale-[1.02] rounded-xl !h-11 ease-in transition-transform',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary-hover',
        ghost: 'hover:bg-muted hover:text-accent-foreground ',
        link: 'text-primary underline-offset-4 hover:underline',
        sidebar:
          'hover:bg-muted overflow-hidden font-medium w-full text-foreground rounded-md !h-7 justify-start gap-2 text-sm',
        stop: 'text-destructive-solid bg-destructive',
      },
      size: {
        default: 'h-10 px-4 py-2',
        special: 'px-2 py-1 h-7',
        xs: 'px-2 py-1 h-8 ',
        smaller: 'px-2 py-1 h-7',
        xxs: 'px-1 py-1 h-[22px] text-xs gap-1 rounded-[6px]',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 px-4 rounded-xl',
        icon: 'h-7 w-7',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  loadingClassName?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading,
      loadingClassName,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        disabled={props.disabled ?? isLoading}
        className={cn(
          buttonVariants({ variant, size, className }),
          isLoading && '!text-transparent disabled:opacity-100',
        )}
        ref={ref}
        {...props}
      >
        {props.children}

        {isLoading && (
          <span
            className={cn(
              buttonVariants({ variant, size, className: loadingClassName }),
              'absolute z-10 inset-0 size-full !h-full',
            )}
          >
            <LoadingSpinner variant="small" color="gray" />
          </span>
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
